/**
 * Parse and validate S3 keys for prior processing results.
 *
 * Canonical layouts:
 *   tenants/{lab_id}/results/{subid}/{dataset_id}/results.json
 *   results/{subid}/{dataset_id}/results.json
 */

/**
 * @param {string} s3Key
 * @returns {{ labId: string|null, subid: string, datasetId: string }|null}
 */
export function parseResultsS3Key(s3Key) {
  if (!s3Key || typeof s3Key !== 'string') {
    return null
  }

  const parts = s3Key.replace(/^\/+|\/+$/g, '').split('/')
  if (!parts.length || parts[parts.length - 1] !== 'results.json') {
    return null
  }

  if (parts.length === 4 && parts[0] === 'results') {
    return { labId: null, subid: parts[1], datasetId: parts[2] }
  }

  if (parts.length === 6 && parts[0] === 'tenants' && parts[2] === 'results') {
    return { labId: parts[1], subid: parts[3], datasetId: parts[4] }
  }

  return null
}

/**
 * Resolve subid/datasetId from API fields or by parsing s3_key.
 * @param {{ s3_key?: string, subid?: string, dataset_id?: string }} result
 */
export function resolveJobIdentityFromResult(result) {
  const fromKey = result?.s3_key ? parseResultsS3Key(result.s3_key) : null
  return {
    subid: result?.subid || fromKey?.subid || 'unknown',
    dataset_id: result?.dataset_id || fromKey?.datasetId || 'unknown',
    lab_id: fromKey?.labId || null,
  }
}

/**
 * Ensure configuredData identity matches the prior results S3 key path.
 * @param {{ subid?: string, dataset_identifier?: string, dataset_id?: string, s3_key?: string, prior_job_s3_key?: string }} configuredData
 */
export function assertJobIdentityMatchesKey(configuredData) {
  const s3Key = configuredData?.prior_job_s3_key || configuredData?.s3_key
  if (!s3Key) {
    return
  }

  const meta = parseResultsS3Key(s3Key)
  if (!meta) {
    console.warn('assertJobIdentityMatchesKey: unparseable S3 key', s3Key)
    return
  }

  const datasetId = configuredData.dataset_identifier || configuredData.dataset_id
  if (configuredData.subid !== meta.subid || datasetId !== meta.datasetId) {
    throw new Error(
      `Job identity mismatch: UI has ${configuredData.subid}/${datasetId}, ` +
        `S3 key has ${meta.subid}/${meta.datasetId}`
    )
  }
}

/**
 * Ensure processing results match the configured upload identity before merging into UI state.
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateProcessingResultsIdentity(processingResults, configuredData, resultsS3Key = null) {
  if (!configuredData) {
    return { valid: false, reason: 'No configured data' }
  }

  const expectedSubid = configuredData.subid
  const expectedDataset =
    configuredData.dataset_identifier || configuredData.dataset_id || '001'

  if (processingResults) {
    if (processingResults.subid && processingResults.subid !== expectedSubid) {
      return {
        valid: false,
        reason: `Results subid ${processingResults.subid} does not match expected ${expectedSubid}`,
      }
    }
    const resultDataset =
      processingResults.dataset_identifier || processingResults.dataset_id
    if (resultDataset && resultDataset !== expectedDataset) {
      return {
        valid: false,
        reason: `Results dataset ${resultDataset} does not match expected ${expectedDataset}`,
      }
    }
  }

  const keyToCheck = resultsS3Key || processingResults?.s3_key
  if (keyToCheck) {
    const meta = parseResultsS3Key(keyToCheck)
    if (meta) {
      if (meta.subid !== expectedSubid || meta.datasetId !== expectedDataset) {
        return {
          valid: false,
          reason: `Results S3 key is for ${meta.subid}/${meta.datasetId}, expected ${expectedSubid}/${expectedDataset}`,
        }
      }
    }
  }

  return { valid: true }
}
